/* eslint-disable react/no-unescaped-entities */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { format, formatDistanceToNowStrict } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { FaTrash } from 'react-icons/fa';
import { IoIosArrowBack, IoMdInformationCircle } from 'react-icons/io';
import { MdEdit } from 'react-icons/md';
import { Link, useHistory, useParams } from 'react-router-dom';

import { Layout, Modal } from '~/components';
import { useDataSheets } from '~/hooks/dataSheets';
import { useScreen } from '~/hooks/screen';
import { useStudent } from '~/hooks/student';
import api from '~/services/api';
import { formatProfileCode } from '~/utils/formatProfileCode';
import { cpfFormatter } from '~/utils/formatter';

import { Datasheet } from './types';

import {
  Container,
  HeaderButtons,
  PageHeader,
  PageHeaderBackButton,
  PageHeaderBackButtonLabel,
  Sidebar,
  Wrapper,
  Grid,
  Section,
  GridRow,
  SectionRow,
  SectionSet,
  Label,
  Content,
  SectionLabel,
  HeaderContainer,
  ModalContainer,
  DeleteModalContainer,
} from './styles';

type RouteParams = {
  userId: string;
  dataSheetId: string;
};

const DataSheet: React.FC = () => {
  const { userId: studentId, dataSheetId } = useParams<RouteParams>();
  const { goBack } = useHistory();
  const { deleteDataSheet } = useDataSheets();
  const { updateScreen } = useScreen();
  const { studentData, updateStudent } = useStudent();

  const [dataSheet, setDataSheet] = useState({} as Datasheet);
  const [selectedComponent, setSelectedComponent] = useState('dataSheet');
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    updateScreen(`dataSheet`);
  }, [updateScreen, dataSheet]);

  // Setta o aluno
  useEffect(() => {
    updateStudent(studentId);
  }, [updateStudent, studentId]);

  const fetchDataSheet = useCallback(async () => {
    const { data } = await api.get(`datasheets/${dataSheetId}`);
    console.log(dataSheetId);
    setDataSheet(data);
  }, [dataSheetId]);

  // Pega os dados da ficha técnica do backend
  useEffect(() => {
    fetchDataSheet();
  }, [fetchDataSheet]);

  function InfoModal() {
    const genderVariations: { [key: string]: string } = {
      male: 'MASCULINO',
      female: 'FEMININO',
      others: 'OUTRO',
      other: 'OUTRO',
      '': 'Não informado',
      null: 'Não informado',
    };

    return (
      <Modal isOpen={showInfoModal} setOpen={setShowInfoModal}>
        <ModalContainer>
          <h1>Informações</h1>

          <section>
            <div>
              <strong>ID</strong>
              <p>{studentId}</p>
            </div>

            <div>
              <strong>Sexo</strong>
              <p>{genderVariations[studentData.student.genre]}</p>
            </div>

            <div>
              <strong>Nome do aluno</strong>
              <p>{studentData.name}</p>
            </div>

            <div>
              <strong>CPF</strong>
              <p>{cpfFormatter(studentData.student.cpf)}</p>
            </div>
          </section>
        </ModalContainer>
      </Modal>
    );
  }

  function DeleteModal() {
    return (
      <Modal isOpen={showDeleteModal} setOpen={setShowDeleteModal}>
        <DeleteModalContainer>
          <h1>Atenção</h1>

          <p>
            Ao clicar em SIM excluirá a ficha técnica.
            <br />
            Tem certeza que deseja continuar?
          </p>

          <div>
            <button type="button" onClick={handleDeleteDataSheet}>
              Sim
            </button>
            <button type="button" onClick={() => setShowDeleteModal(false)}>
              Não
            </button>
          </div>
        </DeleteModalContainer>
      </Modal>
    );
  }

  const dataSheetMemo = useMemo(() => {
    const genderVariations: { [key: string]: string } = {
      male: 'Masculino',
      female: 'Feminino',
      others: 'Outro',
      other: 'Outro',
      '': 'Não informado',
      null: 'Não informado',
    };

    // console.log('dataSheet:', dataSheet);
    return {
      ...dataSheet,
      created_at: format(
        new Date(dataSheet.created_at ?? Date.now()),
        `dd/MM/yyyy`,
      ),
      birth_date: formatDistanceToNowStrict(
        new Date(dataSheet.birth_date ?? Date.now()),
        { locale: ptBR },
      ),
      genre: genderVariations[dataSheet.genre ?? ''],
    };
  }, [dataSheet]);

  // console.log('dataSheetMemo:', dataSheetMemo);

  function DataSheetScreen() {
    return (
      <Section>
        <SectionRow>
          <SectionSet>
            <Label>Data de criação da ficha:</Label>
            <Content>{dataSheetMemo.created_at}</Content>
          </SectionSet>
        </SectionRow>

        <Grid>
          <GridRow>
            <SectionSet>
              <Label>Idade:</Label>
              <Content>{dataSheetMemo.birth_date}</Content>
            </SectionSet>
          </GridRow>

          <GridRow>
            <SectionSet>
              <Label>Gênero:</Label>
              <Content>{dataSheetMemo.genre}</Content>
            </SectionSet>
          </GridRow>

          <GridRow>
            <SectionSet>
              <Label>Altura:</Label>
              <Content>{dataSheetMemo.height || 0} m</Content>
            </SectionSet>
          </GridRow>

          <GridRow>
            <SectionSet>
              <Label>Peso:</Label>
              <Content>{dataSheetMemo.weight || 0} kg</Content>
            </SectionSet>
          </GridRow>

          <GridRow>
            <SectionSet>
              <Label>IMC:</Label>
              <Content>{dataSheetMemo.imc}</Content>
            </SectionSet>
          </GridRow>

          <GridRow>
            <SectionSet>
              <Label>% Fat:</Label>
              <Content>{dataSheetMemo.body_fat || 0} %</Content>
            </SectionSet>
          </GridRow>

          <GridRow>
            <SectionSet>
              <Label>Classificação:</Label>
              <Content>{dataSheetMemo.classification}</Content>
            </SectionSet>
          </GridRow>

          <GridRow>
            <SectionSet>
              <Label>Meta de peso:</Label>
              <Content>{dataSheetMemo.goal_weight || 0} kg</Content>
            </SectionSet>
          </GridRow>

          <GridRow>
            <SectionSet>
              <Label>Nível do aluno:</Label>
              <Content>{dataSheetMemo.student_level}</Content>
            </SectionSet>
          </GridRow>

          <GridRow>
            <SectionSet>
              <Label>Objetivo:</Label>
              <Content>{dataSheetMemo.objective}</Content>
            </SectionSet>
          </GridRow>

          <GridRow>
            <SectionSet>
              <Label>Frequência:</Label>
              <Content>{dataSheetMemo.frequency}</Content>
            </SectionSet>
          </GridRow>

          <GridRow>
            <SectionSet>
              <Label>Tempo disponível:</Label>
              <Content>{dataSheetMemo.available_time}</Content>
            </SectionSet>
          </GridRow>

          <GridRow>
            <SectionSet>
              <Label>Dias da semana:</Label>
              <Content>{dataSheetMemo.week_days}</Content>
            </SectionSet>
          </GridRow>

          <GridRow>
            <SectionSet>
              <Label>Preferência de horário:</Label>
              <Content>{dataSheetMemo.time_preference}</Content>
            </SectionSet>
          </GridRow>
        </Grid>

        <SectionRow>
          <SectionSet>
            <Label>Histórico de saúde:</Label>
            <Content>{dataSheetMemo.health_history}</Content>
          </SectionSet>
        </SectionRow>

        <SectionRow>
          <SectionSet>
            <Label>Histórico de lesão:</Label>
            <Content>{dataSheetMemo.injury_history}</Content>
          </SectionSet>
        </SectionRow>

        <SectionRow>
          <SectionSet>
            <Label>Profile Code:</Label>
            <Content>
              {dataSheetMemo.profile_code
                ? formatProfileCode(dataSheetMemo.profile_code)
                : `Não informado`}
            </Content>
          </SectionSet>
        </SectionRow>
      </Section>
    );
  }

  function AnthropometryScreen() {
    return (
      <Section>
        <SectionRow>
          <SectionSet>
            <Label>Data de criação da ficha:</Label>
            <Content>{dataSheetMemo.created_at}</Content>
          </SectionSet>
        </SectionRow>

        <Grid>
          <GridRow>
            <SectionSet>
              <Label>Idade:</Label>
              <Content>{dataSheetMemo.birth_date}</Content>
            </SectionSet>
          </GridRow>

          <GridRow>
            <SectionSet>
              <Label>Gênero:</Label>
              <Content>{dataSheetMemo.genre}</Content>
            </SectionSet>
          </GridRow>
        </Grid>

        <SectionLabel>Circunferências</SectionLabel>

        <Grid>
          <GridRow>
            <SectionSet>
              <Label>Braço relaxado (cm):</Label>
              <Content>
                Lado direito:{' '}
                {dataSheetMemo.circumference_relaxed_arm_right || 0} cm
              </Content>
              <Content>
                Lado esquerdo:{' '}
                {dataSheetMemo.circumference_relaxed_arm_left || 0} cm
              </Content>
            </SectionSet>
          </GridRow>

          <GridRow>
            <SectionSet>
              <Label>Braço contraído (cm):</Label>
              <Content>
                Lado direito:{' '}
                {dataSheetMemo.circumference_contracted_arm_right || 0} cm
              </Content>
              <Content>
                Lado esquerdo:{' '}
                {dataSheetMemo.circumference_contracted_arm_left || 0} cm
              </Content>
            </SectionSet>
          </GridRow>

          <GridRow>
            <SectionSet>
              <Label>Antebraço (cm):</Label>
              <Content>
                Lado direito: {dataSheetMemo.circumference_forearm_right || 0}{' '}
                cm
              </Content>
              <Content>
                Lado esquerdo: {dataSheetMemo.circumference_forearm_left || 0}{' '}
                cm
              </Content>
            </SectionSet>
          </GridRow>

          <GridRow>
            <SectionSet>
              <Label>Punho (cm):</Label>
              <Content>
                Lado direito: {dataSheetMemo.circumference_fist_right || 0} cm
              </Content>
              <Content>
                Lado esquerdo: {dataSheetMemo.circumference_fist_left || 0} cm
              </Content>
            </SectionSet>
          </GridRow>

          <GridRow>
            <SectionSet>
              <Label>Coxa proximal - prega glútea (cm):</Label>
              <Content>
                Lado direito:{' '}
                {dataSheetMemo.circumference_proximal_thigh_right || 0} cm
              </Content>
              <Content>
                Lado esquerdo:{' '}
                {dataSheetMemo.circumference_proximal_thigh_left || 0} cm
              </Content>
            </SectionSet>
          </GridRow>

          <GridRow>
            <SectionSet>
              <Label>Coxa distal - 10 cm (cm):</Label>
              <Content>
                Lado direito:{' '}
                {dataSheetMemo.circumference_distal_thigh_right || 0} cm
              </Content>
              <Content>
                Lado esquerdo:{' '}
                {dataSheetMemo.circumference_distal_thigh_left || 0} cm
              </Content>
            </SectionSet>
          </GridRow>

          <GridRow>
            <SectionSet>
              <Label>Panturrilha (cm):</Label>
              <Content>
                Lado direito: {dataSheetMemo.circumference_calf_right || 0} cm
              </Content>
              <Content>
                Lado esquerdo: {dataSheetMemo.circumference_calf_left || 0} cm
              </Content>
            </SectionSet>
          </GridRow>
        </Grid>

        <SectionLabel>Dobras cutâneas</SectionLabel>

        <Grid>
          <GridRow>
            <SectionSet>
              <Label>Bíceps (mm):</Label>
              <Content>
                Lado direito: {dataSheetMemo.skin_folds_biceps_right || 0} mm
              </Content>
              <Content>
                Lado esquerdo: {dataSheetMemo.skin_folds_biceps_left || 0} mm
              </Content>
            </SectionSet>
          </GridRow>

          <GridRow>
            <SectionSet>
              <Label>Tríceps (mm):</Label>
              <Content>
                Lado direito: {dataSheetMemo.skin_folds_triceps_right || 0} mm
              </Content>
              <Content>
                Lado esquerdo: {dataSheetMemo.skin_folds_triceps_left || 0} mm
              </Content>
            </SectionSet>
          </GridRow>

          <GridRow>
            <SectionSet>
              <Label>Subescapular (mm):</Label>
              <Content>
                Lado direito: {dataSheetMemo.skin_folds_subscapular_right || 0}{' '}
                mm
              </Content>
              <Content>
                Lado esquerdo: {dataSheetMemo.skin_folds_subscapular_left || 0}{' '}
                mm
              </Content>
            </SectionSet>
          </GridRow>

          <GridRow>
            <SectionSet>
              <Label>Axila média (mm):</Label>
              <Content>
                Lado direito:{' '}
                {dataSheetMemo.skin_folds_middle_armpit_right || 0} mm
              </Content>
              <Content>
                Lado esquerdo:{' '}
                {dataSheetMemo.skin_folds_middle_armpit_left || 0} mm
              </Content>
            </SectionSet>
          </GridRow>

          <GridRow>
            <SectionSet>
              <Label>Supra ilíaca (mm):</Label>
              <Content>
                Lado direito:{' '}
                {dataSheetMemo.skin_folds_heigsupra_iliacaht_right || 0} mm
              </Content>
              <Content>
                Lado esquerdo:{' '}
                {dataSheetMemo.skin_folds_heigsupra_iliacaht_left || 0} mm
              </Content>
            </SectionSet>
          </GridRow>

          <GridRow>
            <SectionSet>
              <Label>Abdomen (mm):</Label>
              <Content>
                Lado direito: {dataSheetMemo.skin_folds_abdomen_right || 0} mm
              </Content>
              <Content>
                Lado esquerdo: {dataSheetMemo.skin_folds_abdomen_left || 0} mm
              </Content>
            </SectionSet>
          </GridRow>

          <GridRow>
            <SectionSet>
              <Label>Peitoral (mm):</Label>
              <Content>
                Lado direito: {dataSheetMemo.skin_folds_breastplate_right || 0}{' '}
                mm
              </Content>
              <Content>
                Lado esquerdo: {dataSheetMemo.skin_folds_breastplate_left || 0}{' '}
                mm
              </Content>
            </SectionSet>
          </GridRow>

          <GridRow>
            <SectionSet>
              <Label>Coxa (mm):</Label>
              <Content>
                Lado direito: {dataSheetMemo.skin_folds_thigh_right || 0} mm
              </Content>
              <Content>
                Lado esquerdo: {dataSheetMemo.skin_folds_thigh_left || 0} mm
              </Content>
            </SectionSet>
          </GridRow>

          <GridRow>
            <SectionSet>
              <Label>Panturrilha (mm):</Label>
              <Content>
                Lado direito: {dataSheetMemo.skin_folds_calf_right || 0} mm
              </Content>
              <Content>
                Lado esquerdo: {dataSheetMemo.skin_folds_calf_left || 0} mm
              </Content>
            </SectionSet>
          </GridRow>

          <GridRow>
            <SectionSet>
              <Label>Total (mm):</Label>
              <Content>{dataSheetMemo.skin_folds_total || 0} mm</Content>
            </SectionSet>
          </GridRow>
        </Grid>
      </Section>
    );
  }

  const handleDeleteDataSheet = useCallback(async () => {
    deleteDataSheet(+dataSheetId);

    goBack();
  }, [dataSheetId, deleteDataSheet, goBack]);

  const sidebarList = [
    {
      value: 'dataSheet',
      title: 'Ficha técnica',
    },
    {
      value: 'anthropometry',
      title: 'Antropometria',
    },
  ];

  const sidebarSelectedComponent: { [key: string]: JSX.Element } = {
    dataSheet: <DataSheetScreen />,
    anthropometry: <AnthropometryScreen />,
  };

  return (
    <Layout title="Aluno">
      <InfoModal />
      <DeleteModal />

      <Container>
        <Sidebar>
          <ul>
            {sidebarList.map((item) => (
              <li
                key={item.value}
                onClick={() => setSelectedComponent(item.value)}
                className={selectedComponent === item.value ? 'active' : ''}
              >
                <p>{item.title}</p>
              </li>
            ))}
          </ul>
        </Sidebar>

        <Wrapper>
          <PageHeader>
            <HeaderContainer>
              <div className="header-container">
                <PageHeaderBackButton onClick={() => goBack()}>
                  <IoIosArrowBack />

                  <PageHeaderBackButtonLabel>
                    ficha técnica
                  </PageHeaderBackButtonLabel>
                </PageHeaderBackButton>

                <HeaderButtons>
                  <Link
                    to={`/professional/students/${studentId}/datasheet/${dataSheetId}/edit`}
                  >
                    <MdEdit color="#000" size="19" />
                  </Link>

                  <button
                    type="button"
                    onClick={() => setShowDeleteModal(true)}
                  >
                    <FaTrash color="#000" size="19" />
                  </button>
                </HeaderButtons>
              </div>

              <div className="info-container">
                <div>
                  <strong>Nome:</strong>
                  {studentData.name}
                </div>

                <button type="button" onClick={() => setShowInfoModal(true)}>
                  <IoMdInformationCircle color="#ffdf22" size={24} />
                </button>
              </div>
            </HeaderContainer>
          </PageHeader>

          {sidebarSelectedComponent[selectedComponent]}
        </Wrapper>
      </Container>
    </Layout>
  );
};

export default DataSheet;
